import { Controller } from "@hotwired/stimulus"
import FieldHandler from "../src/field_handler"

export default class extends Controller {
  static targets = ["dropdown", "input", "bubblesContainer", "bubbleTemplate", "component"]
  static values = {
    name: String,
    searchUrl: String,
    frameId: String,
    fieldName: String,
    form: Boolean,
    old: { // wsparcie dla single_select (value), bulk_action (name_id)
      type: Object,
      default: { send: false, value: "", name_id: "" },
    },
  }

  searchTimeout = null
  count = 0
  connect() {
    console.log("intum select connect")
    this.frame = document.getElementById(this.frameIdValue)
    this.multiple = this.element.dataset.multiple === "true"

    this.fieldHandler = new FieldHandler(this.element, this.fieldNameValue)

    this.open = false
    this.activeIndex = null
    // otwieranie dropdownu po kliknieciu w input
    this.inputTarget.addEventListener("click", this.showDropdown.bind(this))
    this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
    // zamykanie dropdownu po kliknieciu poza nim
    document.addEventListener("click", this.hideDropdown.bind(this))

    this.frameLoaded = false
    // akcje po zaladowaniu frame'a
    this.frame.addEventListener("turbo:frame-load", () => {
      this.frameLoaded = true
      this.toggleSelection()
      this.hideSpinner()
      // reset highlightu po podmianie wynikow
      this.activeIndex = null
    })
  }

  // nawigacja klawiatura po wynikach wyszukiwania (analogicznie do autocomplete)
  // ArrowDown/ArrowUp - poruszanie sie, Enter/Tab - wybor, Escape - zamkniecie
  handleKeydown(event) {
    if (event.key === "Enter") { event.preventDefault() }

    // ArrowDown otwiera zamkniety dropdown
    if (event.key === "ArrowDown" && !this.open) { this.showDropdown() }

    if (!this.open) { return }

    const items = this.resultItems()
    if (items.length === 0) { return }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex == null) ? 0 : Math.min(this.activeIndex + 1, items.length - 1)
      this.highlightActive(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex == null) ? 0 : Math.max(this.activeIndex - 1, 0)
      this.highlightActive(items)
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (this.activeIndex != null && items[this.activeIndex]) {
        event.preventDefault()
        // klik wywoluje data-action: toggleResult + toggleBubble (pelna scieżka wyboru)
        items[this.activeIndex].click()
      }
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.dropdownTarget.classList.add("hidden")
      this.open = false
      this.activeIndex = null
    }
  }

  // wszystkie wybieralne wyniki w turbo frame
  resultItems() {
    return Array.from(this.frame.querySelectorAll("div[data-value]"))
  }

  // podswietla aktywny element (mocniej niz hover) i scrolluje do widoku
  highlightActive(items) {
    items.forEach((el, i) => el.classList.toggle("bg-gray-200", i === this.activeIndex))
    if (items[this.activeIndex]) { items[this.activeIndex].scrollIntoView({ block: "nearest" }) }
  }

  search(event) {
    clearTimeout(this.searchTimeout)
    const silent = event?.detail?.silent

    this.searchTimeout = setTimeout(() => {
      if (this.frame) {
        const fieldIds = Array.from(this.fieldHandler.fields).map(field => field.value).join(",")
        if (!silent) {
          this.showDropdown()
          this.showSpinner()
        }
        if (this.searchUrlValue.includes("?")) {
          this.frame.src = `${this.searchUrlValue}&q=${encodeURIComponent(this.inputTarget.value)}&frame=${this.frameIdValue}&ids=${fieldIds}`
        } else {
          this.frame.src = `${this.searchUrlValue}?q=${encodeURIComponent(this.inputTarget.value)}&frame=${this.frameIdValue}&ids=${fieldIds}`
        }
      } else {
        console.error(`Frame with ID ${this.frameIdValue} not found`)
      }
    }, 1000) // 1s
  }

  findElementByDataAttribute(container, attribute, value) {
    const attributeName = attribute.replace("data-", "")
    return Array.from(container.querySelectorAll(`div[${attribute}]`))
      .find(el => el.dataset[attributeName] === value)
  }

  toggleResult(event) {
    const value = this.getFieldValue(event)
    const searchItem = this.findElementByDataAttribute(this.frame, "data-value", value)
    console.log("searchItem:", searchItem)
    console.log("Toggling:", searchItem, value)

    // single-select: klikniecie aktualnie wybranej opcji = re-submit z ta sama wartoscia.
    // Domyslny toggle-off (handleFieldChange usuwa + dodaje "") submitowal pusta wartosc
    // i czyscil pole. Zamiast tego ponownie wysylamy formularz z aktualnym value, co
    // pozwala np. ponownie zarchiwizowac email z zamkajacym statusem po reopen.
    // Czyszczenie wartosci w single-select robi sie przez opcje "Bez przypisania".
    if (!this.multiple && this.fieldHandler.hiddenFieldExists(this.fieldNameValue, value)) {
      event.intumReSubmit = true // sentinel — toggleBubble pominie usuniecie bombelka
      event.preventDefault()
      if (this.formValue) {
        const form = this.element.closest("form")
        if (form) {
          this.addOldValue()
          form.requestSubmit()
        }
      }
      return
    }

    this.select(searchItem, value)
  }

  toggleSelection() {
    console.log("toggleSelection")
    this.fieldHandler.fields.forEach(field => {
      const result = this.findElementByDataAttribute(this.frame, "data-value", field.value)
      if (result) this.toggleSearchFieldDetails(result)
    })
  }

  toggleBubble(event) {
    if (event.intumReSubmit) return // single-select same-pick re-submit — nie ruszaj bombelka
    const id = this.getFieldValue(event)

    // dla single-select usuwamy stare bąbelki (różne od klikniętego id) - to oznacza zmianę wyboru.
    // bąbelek o tym samym id pomijamy - toggleResult (odpalany przed toggleBubble) już zatoggleował
    // jego stan w hidden fields i ikonach; my tylko usuniemy go z DOM jeśli ma być odznaczony.
    if (!this.multiple) {
      this.bubblesContainerTarget.querySelectorAll("div[data-bubble]").forEach(bubble => {
        if (bubble.dataset.bubble !== id) this.removeBubbleElement(bubble)
      })
    }

    const bubble = this.findElementByDataAttribute(this.bubblesContainerTarget, "data-bubble", id)
    console.log("toggling bubble:", bubble, id)

    if (bubble) this.removeBubble(event)
    else this.addBubble(event)
  }

  // usuwa bąbelek niezależnie od jego templatu (multi-mode ma button X, single-mode nie ma)
  // przechodzi przez select() — w single-select po zamianie wartosci toggleResult dodaje nowa wartosc
  // (fields=2, no-submit), potem usuniecie starego baka tutaj zbije fields do 1 i select() submituje formularz
  removeBubbleElement(bubble) {
    const value = bubble.dataset.bubble
    const searchItem = this.findElementByDataAttribute(this.frame, "data-value", value)
    this.select(searchItem, value)
    bubble.remove()
  }

  addBubble(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.value
    const name = event.currentTarget.querySelector("#name").querySelector("span").textContent
    const bubble = this.bubbleTemplateTarget.content.cloneNode(true)

    bubble.querySelector("#bubble").dataset.bubble = id
    bubble.querySelector("#name").textContent = name
    this.bubblesContainerTarget.prepend(bubble)
    const clearButton = this.bubblesContainerTarget.querySelector("#clearAllButton")
    if (clearButton) { clearButton.classList.remove("hidden") }
    this.clearInput()
  }

  removeBubble(event) {
    event.preventDefault()
    console.log("Removing bubble")
    // zarowno we frame istnieje div[data-value] jak i w bubble istnieje div[data-bubble], maja one korespondujace ze soba wartosci
    const value = this.getFieldValue(event)
    const field = this.findElementByDataAttribute(this.bubblesContainerTarget, "data-bubble", value)

    field.remove()
    // po usunięciu bąbelka focus na search input - user może od razu szukać/wpisywać
    this.inputTarget.focus()
  }

  // klik w wybrany single_value bubble: zamieniamy go na tekst w inpucie, zeby user
  // mogl edytowac/skasowac jak normalnie wpisany query. Po usunieciu bubble input
  // odsłania się (znika reguła group-has-[data-bubble]:hidden), dropdown otwiera,
  // tekst zaznaczony - user moze od razu pisac na nowo lub modyfikowac.
  editBubble(event) {
    event.preventDefault()
    event.stopPropagation()
    const bubble = event.currentTarget.closest("div[data-bubble]")
    if (!bubble) { return }

    const name = bubble.querySelector("#name").textContent.trim()

    // usuwa bąbelek + czyści hidden field (przez select() → handleFieldChange)
    this.removeBubbleElement(bubble)

    this.inputTarget.value = name
    this.showDropdown()
    this.inputTarget.focus()
    // kursor na koncu tekstu — user moze dopisywac/kasowac od tyłu, bez zaznaczenia
    this.inputTarget.setSelectionRange(name.length, name.length)
    // celowo bez search() — wyszukiwanie startuje dopiero gdy user faktycznie zmieni tekst
    // (przez data-action="input->intum-select#search" na inpucie)
  }

  select(field, value) {
    console.log("Selecting:", field, value)
    this.fieldHandler.handleFieldChange(value)
    if (this.hasComponentTarget) { this.componentTarget.dispatchEvent(new Event("change", { bubbles: true })) }
    // ukrywamy elementy z searcha tylko gdy frame jest juz załadowany
    if (this.frameLoaded) { this.toggleSearchFieldDetails(field) }

    // chcemy submitowac form tylko wtedy gdy jest to single select i jest tylko jeden element wyszukany
    // odpalamy toggleResult w _bubble.html.erb i zeby uniknac submitowania formularza, gdy sa jeszcze dwie wartosci w hidden fields
    if (this.formValue && this.fieldHandler.fields.length === 1) {
      const form = this.element.closest("form")
      if (form) {
        this.addOldValue() // opcjonalne dodanie pierwotnej wartości do formularza
        form.requestSubmit()
      }
    }
  }

  clearBubbles(_event) {
    const event1 = new Event("click")
    console.log("Removing all bubbles")
    this.bubblesContainerTarget.querySelectorAll("div[data-bubble]").forEach(bubble => {
      bubble.querySelector("button").dispatchEvent(event1)
    })

    this.bubblesContainerTarget.querySelector("#clearAllButton").classList.add("hidden")
  }

  toggleSearchFieldDetails(field) {
    // czasami nie ma już w selectcie tego co próbujemy togglować
    // na przykład po wyszukaniu kogoś innego, obecnie wybrana osoba nie jest już na liście
    if (!field) { return }

    this.toggleCrossIcon(field)
    this.toggleTickIcon(field)
  }

  disconnect() {
    document.removeEventListener("click", this.hideDropdown.bind(this))
  }

  toggleTickIcon(target) { target.querySelector(".tick-icon").classList.toggle("hidden") }
  toggleCrossIcon(target) { target.querySelector(".cross-icon").classList.toggle("hidden") }
  hideSpinner() { this.element.querySelector("#spinner").classList.add("hidden") }
  showSpinner() { this.element.querySelector("#spinner").classList.remove("hidden") }
  showDropdown(event) {
    // stopPropagation potrzebne gdy showDropdown jest wywoływane z data-action na divku
    // (np. _single_value w trybie single-select). Bez tego document-level hideDropdown
    // sprawdza event.target - jeśli to nie INPUT/BUTTON, zamyka dropdown od razu.
    if (event) event.stopPropagation()
    if (this.open) { return }
    console.log("showDropdown", this.open)
    this.dropdownTarget.classList.remove("hidden")
    this.open = true
  }

  toggleDropdown() {
    console.log("toggleDropdown", this.open)
    this.dropdownTarget.classList.toggle("hidden")
    this.open = !this.open
    if (this.open) { this.inputTarget.focus() }
  }

  clearInput() { this.inputTarget.value = "" }
  hideDropdown(event) {
    if (!this.open) { return }

    // sprawdzamy czy event.target jest inputem lub buttonem, ale zahaczamy tez o elementy w srodku buttona, np divy
    const isInputOrButton = (element, depth = 5) => {
      if (!element || depth === 0) return false
      if (element.tagName === "INPUT" || element.tagName === "BUTTON") return true
      return isInputOrButton(element.parentElement, depth - 1)
    }

    if (!this.element.contains(event.target) || !isInputOrButton(event.target)) {
      console.log("hiding dropdown", this.open)
      this.dropdownTarget.classList.add("hidden")
      this.open = false
    }
  }

  getFieldValue(event) {
    const closestDiv = event.currentTarget.closest("div[data-value]") || event.currentTarget.closest("div[data-bubble]")
    return closestDiv.dataset.value || closestDiv.dataset.bubble
  }

  // wsparcie wysyłania pierwotnej wartości pola / pól dla single_select, bulk_action
  addOldValue() {
    if (this.oldValue["send"] === false) { return }

    if (this.nameValue === "single_select") {
      this.fieldHandler.addHiddenField(`old_${this.fieldNameValue}`, this.oldValue["value"])
    }

    if (this.nameValue === "bulk_action") {
      const fieldName = this.fieldNameValue.endsWith("[]") ? this.fieldNameValue.slice(0, -2) : this.fieldNameValue

      // dla poprawnego działania bulk action z opcją old, pozyskujemy z widoku dokumenty stare wartości
      document.querySelectorAll(`div#bulk_action_old[data-name-id="${this.oldValue["name_id"]}"]`).forEach(el => {
        const id = el.dataset.id
        const oldValue = el.dataset.value
        this.fieldHandler.addHiddenField(`old_${fieldName}[${id}]`, oldValue)
      })
    }
  }
}
